import React from "react";
import { FormattedMessage } from "react-intl";
import { useLanguage } from "../../i18n/LanguageContext";
import { GlobalMenus } from "../../model/menu";
import { SanityMenuItem } from "../../model/common";
import { useSocialConfigData } from "../../model/social";
import AnchorButton from "../AnchorButton/AnchorButton";
import { Col, Row, Wrapper } from "../Grid";
import { internalLinkUrl } from "../InternalLink/InternalLink";
import IsDesktop from "../IsDesktop/IsDesktop";
import Link from "../Link/Link";
import styles from "./Footer.module.scss";
import Text from "../Text/Text";
import GradientLine from "../GradientLine/GradientLine";
import FacebookIcon from "../../icons/FacebookIcon";
import LinkedInIcon from "../../icons/LinkedInIcon";
import PlainButton from "../Button/PlainButton/PlainButton";
import { manageCookies } from "../../utils/compliance";
import InstagramIcon from "../../icons/InstagramIcon";
import ThreadsIcon from "../../icons/ThreadsIcon";
import BlueskyIcon from "../../icons/BlueskyIcon";
import YoutubeIcon from "../../icons/YoutubeIcon";
import Divider from "../Divider/Divider";
import classNames from "classnames";

interface FooterProps {
  menus: GlobalMenus;
}

const Footer = ({ menus }: FooterProps) => {
  const { footerMenus, copyright } = menus;
  const {
    facebook,
    linkedin,
    instagram,
    threads,
    bluesky,
    youtube
  } = useSocialConfigData();
  return (
    <>
      <footer className={styles.root}>
        <Wrapper>
          <div className={styles.sections}>
            {footerMenus.map((menu, index) => (
              <div key={menu.title} className={styles.section}>
                <Text variant="footerHeading">{menu.title}</Text>
                <ul>
                  {menu.items.map((menuItem, index) => (
                    <Text
                      key={index}
                      variant="footerDefault"
                      as="li"
                      gutterBottom={true}
                    >
                      <MenuLink item={menuItem} />
                    </Text>
                  ))}
                  {index === 2 && (
                    <Text
                      key={index}
                      variant="footerDefault"
                      as="li"
                      gutterBottom={true}
                    >
                      <PlainButton
                        style={{
                          padding: 0,
                          borderRadius: 0,
                          color: "black"
                        }}
                        onClick={manageCookies}
                      >
                        <Text as="span" variant="footerDefault">
                          Cookies
                        </Text>
                      </PlainButton>
                    </Text>
                  )}
                </ul>
              </div>
            ))}
            <div
              className={classNames(styles.section, styles.socialNewsletter)}
              key="social-newsletter"
            >
              <div>
                <Text variant="footerHeading">
                  <FormattedMessage id="sign-up-newsletter" />
                </Text>
                <AnchorButton to="https://mailchi.mp/microbit/newsletter">
                  <FormattedMessage id="join-us" />
                </AnchorButton>
              </div>
              <div>
                <Text variant="footerHeading">
                  <FormattedMessage id="follow-us" />
                </Text>
                <div className={styles.socialIcons}>
                  <Link
                    to={`https://www.linkedin.com/company/${linkedin.handle}`}
                    aria-label="LinkedIn"
                  >
                    <LinkedInIcon height={30} width={30} />
                  </Link>
                  <Link
                    to={`https://instagram.com/${instagram.handle}`}
                    aria-label="Instagram"
                  >
                    <InstagramIcon height={30} width={30} />
                  </Link>
                  <Link
                    to={`https://www.facebook.com/${facebook.handle}`}
                    aria-label="Facebook"
                  >
                    <FacebookIcon height={30} width={30} />
                  </Link>
                  <Link
                    to={`https://www.threads.net/${threads.handle}`}
                    aria-label="Threads"
                  >
                    <ThreadsIcon height={30} width={30} />
                  </Link>
                  <Link
                    to={`https://bsky.app/profile/${bluesky.handle}`}
                    aria-label="Bluesky"
                  >
                    <BlueskyIcon height={30} width={30} />
                  </Link>
                  <Link
                    to={`https://www.youtube.com/${youtube.handle}`}
                    aria-label="Youtube"
                  >
                    <YoutubeIcon height={30} width={30} />
                  </Link>
                </div>
              </div>
            </div>
          </div>
          <Divider className={styles.divider} />
          <Row>
            <Col className={styles.subFooter}>
              <ul>
                <Text variant="defaultLight" as="li">
                  &#169; Micro:bit Educational Foundation
                </Text>
                {copyright && copyright.length > 0
                  ? copyright.map((menuItem, index) => (
                      <Text key={index} variant="footerDefault" as="li">
                        <MenuLink item={menuItem} />
                      </Text>
                    ))
                  : null}
              </ul>
            </Col>
          </Row>
        </Wrapper>
      </footer>
      <IsDesktop>
        <GradientLine />
      </IsDesktop>
    </>
  );
};

const MenuLink = ({
  item: { internalLink, externalLink, title, query, fragment }
}: {
  item: SanityMenuItem;
}) => {
  const language = useLanguage();
  const to = internalLink
    ? internalLinkUrl(internalLink, query, fragment, language)
    : externalLink
    ? externalLink
    : "";
  return <Link to={to}>{title}</Link>;
};

export default Footer;
